import { ReactNode } from "react";
import clsx from "clsx";

type Props = {
  handler: () => void;
  title?: string;
  icon?: ReactNode;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "primary" | "success" | "info" | "warning" | "danger";
};

const variantClasses = {
  default:
    "border-gray-200 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white",

  primary:
    "border-brand-200 bg-brand-50 text-brand-600 hover:bg-brand-100 dark:border-brand-700 dark:bg-brand-900/20 dark:text-brand-400",

  success:
    "border-green-200 bg-green-50 text-green-600 hover:bg-green-100 dark:border-green-700 dark:bg-green-900/20 dark:text-green-400",

  info: "border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-400",

  warning:
    "border-yellow-200 bg-yellow-50 text-yellow-600 hover:bg-yellow-100 dark:border-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400",

  danger:
    "border-red-200 bg-red-50 text-red-600 hover:bg-red-100 dark:border-red-700 dark:bg-red-900/20 dark:text-red-400",
};

const sizeClasses = {
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-12 w-12",
};

const IconButton = ({
  handler,
  title,
  icon,
  className = "",
  disabled = false,
  loading = false,
  size = "md",
  variant = "default",
}: Props) => {
  return (
    <div className="group relative inline-flex">
      <button
        type="button"
        disabled={disabled || loading}
        onClick={handler}
        className={clsx(
          "relative flex items-center justify-center rounded-full border transition-all duration-200",
          "focus:outline-none focus:ring-2 focus:ring-brand-500/30",
          "active:scale-95",
          sizeClasses[size],
          variantClasses[variant],

          (disabled || loading) && "cursor-not-allowed opacity-50",

          className
        )}
      >
        {loading ? <div className="loader w-3 text-brand-500" /> : icon}
      </button>

      {!!title && (
        <span
          className="
            pointer-events-none
            absolute
            left-1/2
            top-full
            z-50
            mt-2
            -translate-x-1/2
            whitespace-nowrap
            rounded-md
            bg-gray-900
            px-2.5
            py-1
            text-[11px]
            text-white
            opacity-0
            shadow-lg
            transition-all
            duration-200
            group-hover:translate-y-0
            group-hover:opacity-100
            dark:bg-gray-700
          "
        >
          {title}
        </span>
      )}
    </div>
  );
};

export default IconButton;
